<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { inject, computed, watch, onMounted } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { useMachine } from "@xstate/vue"
import { useEventBus } from "@vueuse/core"
import type { NotificationEvent } from "@types"
import type { Services } from "@services"
import { useAuthStore } from "@core/stores/auth.store"
import { useConfigStore } from "@core/stores/config.store"
import {
    createSupportModalMachine,
    SUPPORT_OPTIONS,
    type SupportModalContext,
    type SupportOptionValue,
} from "./support-modal.machine"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            support: {
                heading1: "Comment pouvons-nous vous aider ?",
                heading_bug1: "Signaler un bug",
                heading_bug2:
                    "Quel est le niveau d’urgence du bug que vous souhaitez signaler ?",
                heading_feedback1: "Donner votre avis",
                heading_feedback2: "Décrivez votre suggestion ou votre avis.",
                heading_issue1: "Signaler un problème technique",
                option_bug: "Signaler un bug",
                option_doc: "Consulter la documentation",
                option_feedback: "Donner votre avis",
                placeholder: "Décrivez votre {type}...",
                level_low_title: "Faible",
                level_low_subtitle: "Gêne mineure",
                level_medium_title: "Moyen",
                level_medium_subtitle: "Problème notable",
                level_high_title: "Élevé",
                level_high_subtitle: "Bloquant",
                buttons: {
                    send: "Envoyer",
                    previous: "Retour",
                    doc: "Documentation",
                },
                success_notification: "Votre {type} a été envoyé avec succès.",
                error_message: "Une erreur est survenue",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /** Identifiant unique de la modal */
    modalId?: string
    /** Affiche uniquement le formulaire de bug */
    onlyBug?: boolean
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    modalId: "modal-support",
    onlyBug: false,
})

const visible = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const authStore = useAuthStore()
const configStore = useConfigStore()
const bus = useEventBus<NotificationEvent>("notification")
const services = inject<Services>("services")

/** Fermeture de la modal. */
function closeModal(): void {
    visible.value = false
}

/** Émet une notification de succès puis ferme la modal. */
function notifySuccessAndClose(type: string): void {
    bus.emit({
        group: "notifications",
        severity: "success",
        title: t("support.success_notification", { type }),
        message: t("support.success_notification", { type }),
        life: 5000,
    })
    closeModal()
}

/**
 * Construit l'input de la machine avec les getters et services.
 */
function buildMachineInput() {
    return {
        onlyBug: props.onlyBug,
        getTitle: (key: string) => t(`support.${key}`),
        getCurrentPage: () => route?.fullPath ?? "",
        getUsername: () => authStore.username || "",
        formatDate: (date: Date) =>
            date.toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            }),
        sendFeedback: (data: Parameters<Services["$sendFeedback"]>[0]) =>
            services!.$sendFeedback(data),
        reportBugIssue: (data: Parameters<Services["$reportBugIssue"]>[0]) =>
            services!.$reportBugIssue(data),
        onSuccess: notifySuccessAndClose,
    }
}

const machineInput = buildMachineInput()
const machine = createSupportModalMachine(machineInput)
// Options passées via rest pour satisfaire le typage conditionnel de useMachine (binding pattern).
const { snapshot, send } = useMachine(machine, ...[{ input: machineInput }])

/**
 * Contexte de la machine avec valeur par défaut pour éviter les erreurs avant initialisation.
 */
const getDefaultContext = (): SupportModalContext => ({
    title: "",
    subtitle: "",
    inputLevel: "",
    inputMessage: "",
    value: "" as SupportOptionValue,
    message: "",
    error: "",
    disabledButton: false,
})

/** Contexte courant de la machine (sûr pour le template). */
const getContext = computed(
    () => snapshot.value?.context ?? getDefaultContext()
)

// ================================
// DONNÉES CALCULÉES
// ================================
/**
 * Options du menu principal (bug, doc, feedback). « Consulter la
 * documentation » ouvre le site de l'éditeur : elle n'est proposée que sur le
 * scope par défaut, jamais en marque blanche (05/10/2026).
 */
const getMenuOptions = computed(() =>
    [
        {
            value: SUPPORT_OPTIONS.BUG,
            label: t("support.option_bug"),
            icon: "fa-solid fa-bug",
        },
        {
            value: SUPPORT_OPTIONS.DOC,
            label: t("support.option_doc"),
            icon: "pi pi-book",
        },
        {
            value: SUPPORT_OPTIONS.FEEDBACK,
            label: t("support.option_feedback"),
            icon: "fa-solid fa-handshake",
        },
    ].filter(
        (opt) => opt.value !== SUPPORT_OPTIONS.DOC || configStore.isDefaultScope
    )
)

/** Niveaux de gravité pour le bug */
const getLevelOptions = computed(() => [
    {
        value: "low",
        title: t("support.level_low_title"),
        subtitle: t("support.level_low_subtitle"),
        variant: "low",
    },
    {
        value: "medium",
        title: t("support.level_medium_title"),
        subtitle: t("support.level_medium_subtitle"),
        variant: "medium",
    },
    {
        value: "high",
        title: t("support.level_high_title"),
        subtitle: t("support.level_high_subtitle"),
        variant: "high",
    },
])

/** Titre du bouton Retour (alternance bug/feedback) */
const getReturnButtonLabel = computed(() =>
    getContext.value.value === SUPPORT_OPTIONS.BUG
        ? t("support.option_feedback")
        : t("support.option_bug")
)

const getIsHelpMenu = computed(() => snapshot.value?.matches("menu") ?? false)
const getIsLoading = computed(
    () => snapshot.value?.matches("submitting") ?? false
)
const getShowForm = computed(
    () =>
        (snapshot.value?.matches("form") ?? false) ||
        (snapshot.value?.matches("submitting") ?? false)
)
const getShowButtons = computed(
    () => !(snapshot.value?.matches("menu") ?? true)
)
const getCanSubmit = computed(() => {
    const ctx = getContext.value
    if (snapshot.value?.matches("submitting")) return false
    if (ctx.value === SUPPORT_OPTIONS.BUG) {
        return ctx.inputMessage.trim() !== "" && ctx.inputLevel !== ""
    }
    return ctx.inputMessage.trim() !== ""
})

// ================================
// WATCHERS
// ================================
watch(visible, (isVisible) => {
    if (isVisible) {
        send({ type: "OPEN" })
    }
})

// ================================
// INTERACTIONS
// ================================
function onClose(): void {
    send({ type: "RESET" })
    visible.value = false
}

function onSelectOption(value: string): void {
    if (value === SUPPORT_OPTIONS.DOC) {
        window.open("https://www.homeys.fr/support", "_blank")
        return
    }
    send({ type: value as "BUG" | "FEEDBACK" })
}

function onPrevious(): void {
    send({ type: "PREVIOUS" })
}

function onDoc(): void {
    window.open("https://www.homeys.fr/support", "_blank")
}

function onSubmit(): void {
    send({ type: "SUBMIT" })
}

function onInputMessage(value: string): void {
    send({ type: "INPUT_MESSAGE", value })
}

function onInputLevel(value: string): void {
    send({ type: "INPUT_LEVEL", value })
}

// ================================
// LIFECYCLE
// ================================
onMounted(() => {
    if (visible.value) {
        send({ type: "OPEN" })
    }
})
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :closable="true"
        dismissable-mask
        class="max-w-[57rem] w-full"
        @update:visible="(v: boolean) => !v && onClose()"
    >
        <!-- ================================ -->
        <!-- HEADER -->
        <!-- ================================ -->
        <template #header>
            <h2 class="text-2xl mx-auto font-semibold text-primary">
                {{ getContext.title }}
            </h2>
        </template>

        <!-- ================================ -->
        <!-- CONTENU PRINCIPAL -->
        <!-- ================================ -->
        <div class="mx-auto flex max-w-[57rem] flex-col gap-4">
            <p
                v-if="getContext.subtitle"
                class="mb-2 text-center text text-surface-600"
            >
                {{ getContext.subtitle }}
            </p>

            <!-- Menu : Bug, Doc, Feedback -->
            <div v-if="getIsHelpMenu && !onlyBug" class="flex flex-wrap gap-3">
                <BaseButton
                    v-for="opt in getMenuOptions"
                    :key="opt.value"
                    :label="opt.label"
                    class="min-w-[10rem] flex-1"
                    severity="secondary"
                    size="large"
                    @click="onSelectOption(opt.value)"
                >
                    <template #icon>
                        <BaseIcon :icon="opt.icon" />
                    </template>
                </BaseButton>
            </div>

            <!-- Niveaux de gravité (bug) -->
            <div
                v-if="getContext.value === SUPPORT_OPTIONS.BUG && getShowForm"
                class="flex flex-col gap-3 sm:flex-row"
            >
                <div
                    v-for="level in getLevelOptions"
                    :key="level.value"
                    class="flex cursor-pointer items-center gap-2 rounded border p-4 transition-colors w-full"
                    :class="{
                        'border-primary-500 text-primary-700':
                            level.variant === 'low',
                        'border-amber-500 text-amber-800':
                            level.variant === 'medium',
                        'border-red-500 text-red-800': level.variant === 'high',
                        'bg-primary-50':
                            level.variant === 'low' &&
                            getContext.inputLevel === level.value,
                        'bg-amber-50':
                            level.variant === 'medium' &&
                            getContext.inputLevel === level.value,
                        'bg-red-50':
                            level.variant === 'high' &&
                            getContext.inputLevel === level.value,
                        'border-surface-200 text-inherit hover:bg-surface-50':
                            !['low', 'medium', 'high'].includes(level.variant),
                    }"
                    @click="onInputLevel(level.value)"
                >
                    <BaseRadioButton
                        :model-value="getContext.inputLevel"
                        :value="level.value"
                        :name="'level-' + level.value"
                        :input-id="'level-' + level.value"
                    />
                    <div class="flex flex-col">
                        <span class="font-medium">{{ level.title }}</span>
                        <span class="text-sm opacity-90">{{
                            level.subtitle
                        }}</span>
                    </div>
                </div>
            </div>

            <!-- Zone de message -->
            <div v-if="getShowForm" class="flex flex-col gap-2">
                <BaseTextarea
                    :model-value="getContext.inputMessage"
                    :placeholder="
                        t('support.placeholder', {
                            type:
                                getContext.value === SUPPORT_OPTIONS.BUG
                                    ? 'bug'
                                    : getContext.value ===
                                        SUPPORT_OPTIONS.FEEDBACK
                                      ? 'avis'
                                      : 'problème',
                        })
                    "
                    rows="5"
                    class="w-full"
                    @update:model-value="onInputMessage"
                />
            </div>

            <!-- Boutons d'action -->
            <div
                v-if="getShowButtons"
                class="flex flex-wrap items-center justify-between gap-2"
            >
                <BaseButton
                    v-if="!onlyBug"
                    :label="getReturnButtonLabel"
                    severity="secondary"
                    :disabled="getIsLoading"
                    @click="onPrevious"
                />
                <div class="flex flex-1 justify-end gap-2">
                    <BaseButton
                        v-if="configStore.isDefaultScope"
                        :label="t('support.buttons.doc')"
                        severity="secondary"
                        :disabled="getIsLoading"
                        icon="pi pi-book"
                        @click="onDoc"
                    />
                    <BaseButton
                        :label="t('support.buttons.send')"
                        :loading="getIsLoading"
                        :disabled="!getCanSubmit"
                        icon="pi pi-send"
                        @click="onSubmit"
                    />
                </div>
            </div>

            <!-- Message d'erreur -->
            <BaseMessage
                v-if="getContext.message"
                severity="error"
                class="mt-4"
            >
                {{ getContext.message }}
            </BaseMessage>
        </div>
    </BaseDialog>
</template>
